import { ApiHelper } from 'app/api/api-helper'
import { logger } from 'app/logging/Logger'

/**
 * ACA-16: send a completed form to ACC via the backend `tuhiSubmitAccDocument`
 * Lambda (`POST /accDocuments/`). The Lambda renders the same PDF clinicians
 * download, builds ACC's document metadata from the consult's attached patient
 * and uploads it. On DEV it talks to a fake ACC until ACC issues an API key.
 *
 * The Lambda's replies map onto {@link AccSubmissionResult}:
 * - 200 `receipted`            -> receipted
 * - 200 `rejected` (ACC 400)   -> rejected, with ACC's per-field errors
 * - 422 `invalid`              -> invalid: nothing was sent (missing/invalid fields)
 * - 401 / 403 / 404 / 503 / 5xx -> error, with a clinician-facing message
 */

/** ACC's mandatory metadata fields, as the backend names them. */
export type AccMandatoryField =
    | 'contentType'
    | 'claimReferenceNumber'
    | 'clientFirstName'
    | 'clientLastName'
    | 'clientDOB'
    | 'fileExtension'

export type AccSubmissionResult =
    | { kind: 'receipted'; mode: 'acc' | 'fake'; createdAt: string | null }
    | {
          kind: 'rejected'
          message: string | null
          fields: { field?: string; message?: string }[]
      }
    | {
          kind: 'invalid'
          missing: string[]
          invalid: { field: string; reason: string }[]
      }
    | { kind: 'error'; statusCode: number | null; message: string }

export interface AccSubmissionRequest {
    accessToken: string
    formId: string
    claimReferenceNumber: string
    contentType: string
}

/**
 * Same tidy-up and plausibility rule as the backend metadata builder
 * (`AccDocumentMetadataBuilder.normaliseClaimReference` /
 * `isPlausibleClaimReference`), so the clinician is told before sending.
 * The backend re-checks; this is only for early feedback.
 */
export const normaliseClaimReference = (value: string): string =>
    (value ?? '').replace(/\s+/g, '').toUpperCase()

const CLAIM_REFERENCE_PATTERN = /^[A-Z]{2,4}\d{4,10}$/

export const isPlausibleClaimReference = (value: string): boolean =>
    value.length <= 12 && CLAIM_REFERENCE_PATTERN.test(value)

/** Clinician-facing wording for each field the backend can report. */
export const ACC_FIELD_LABELS: Record<string, string> = {
    contentType: 'ACC document type',
    claimReferenceNumber: 'ACC claim number',
    clientFirstName: "Patient's first name",
    clientLastName: "Patient's last name",
    clientDOB: "Patient's date of birth",
    fileExtension: 'Document file type',
    providerFirstName: 'Your first name',
    providerLastName: 'Your last name',
    dateOfService: 'Consult date',
}

export const accFieldLabel = (field: string | undefined): string =>
    (field && ACC_FIELD_LABELS[field]) || field || 'Unknown field'

const ERROR_MESSAGES: Record<number, string> = {
    400: 'The request was incomplete. Please try again.',
    401: 'Your session has expired. Please log in again and retry.',
    403: 'You can only send forms from your own consults.',
    404: 'This form could not be found. It may have been deleted.',
    413: 'This form is too large for ACC (over 20 MB).',
    503: 'Sending to ACC is not set up in this environment yet.',
}

const GENERIC_ERROR =
    'The form could not be sent to ACC. Please try again or contact support.'

/**
 * A 403 from API Gateway itself (bad or stale request signature, IAM denial,
 * unknown route) rather than from the Lambda. The Lambda's own 403s send a
 * JSON-encoded plain string via buildErrorResponse. The gateway sets an
 * `x-amzn-ErrorType` header and sends an object (`{"message": ...}`), or
 * nothing. Its signature-error body embeds raw newlines, so it is NOT valid
 * JSON and reaches us as text starting with `{`; check for that too.
 */
export const GATEWAY_FORBIDDEN_ERROR =
    'Tuhi could not authorise this request, so nothing was sent to ACC. Please refresh the page and try again. If it keeps happening, contact support.'

const hasGatewayErrorType = (headers: Record<string, string> | undefined) =>
    !!headers &&
    Object.keys(headers).some((k) => k.toLowerCase() === 'x-amzn-errortype')

const isGatewayForbidden = (
    statusCode: number | null,
    body: unknown,
    headers?: Record<string, string>
): boolean => {
    if (statusCode !== 403) return false
    if (hasGatewayErrorType(headers)) return true
    // The Lambda's only 403 shape: a non-empty plain message
    if (typeof body !== 'string' || body.trim() === '') return true
    return body.trim().startsWith('{')
}

interface RestErrorResponse {
    statusCode?: number
    body?: string
    headers?: Record<string, string>
}

const parseBody = (raw: unknown): unknown => {
    if (typeof raw !== 'string') return raw ?? null
    try {
        return JSON.parse(raw)
    } catch {
        return raw
    }
}

/**
 * Map a (status, body) pair from the Lambda to a result. Exported for tests.
 * `body` is the parsed JSON body (or a plain string message).
 */
export const toAccSubmissionResult = (
    statusCode: number | null,
    body: unknown,
    headers?: Record<string, string>
): AccSubmissionResult => {
    const data = (body && typeof body === 'object' ? body : {}) as Record<
        string,
        unknown
    >

    if (statusCode === 200 && data.status === 'receipted') {
        return {
            kind: 'receipted',
            mode: data.mode === 'acc' ? 'acc' : 'fake',
            createdAt:
                typeof data.createdAt === 'string' ? data.createdAt : null,
        }
    }
    if (statusCode === 200 && data.status === 'rejected') {
        return {
            kind: 'rejected',
            message: typeof data.message === 'string' ? data.message : null,
            fields: Array.isArray(data.metadataErrors)
                ? (data.metadataErrors as {
                      field?: string
                      message?: string
                  }[])
                : [],
        }
    }
    if (statusCode === 422 && data.status === 'invalid') {
        return {
            kind: 'invalid',
            missing: Array.isArray(data.missing)
                ? (data.missing as string[])
                : [],
            invalid: Array.isArray(data.invalid)
                ? (data.invalid as { field: string; reason: string }[])
                : [],
        }
    }
    if (isGatewayForbidden(statusCode, body, headers)) {
        return { kind: 'error', statusCode, message: GATEWAY_FORBIDDEN_ERROR }
    }
    const knownMessage = statusCode ? ERROR_MESSAGES[statusCode] : undefined
    return {
        kind: 'error',
        statusCode,
        message: knownMessage ?? GENERIC_ERROR,
    }
}

export const submitFormToAcc = async (
    request: AccSubmissionRequest
): Promise<AccSubmissionResult> => {
    const operation = ApiHelper.buildSubmitAccDocumentOperation({
        ...request,
        claimReferenceNumber: normaliseClaimReference(
            request.claimReferenceNumber
        ),
        contentType: request.contentType.trim(),
    })

    let result: AccSubmissionResult
    try {
        const response = await operation.response
        const body = await response.body.json()
        result = toAccSubmissionResult(response.statusCode ?? 200, body)
    } catch (error) {
        // Amplify throws for any non-2xx reply and attaches the raw response.
        const response = (error as { response?: RestErrorResponse })?.response
        result = toAccSubmissionResult(
            response?.statusCode ?? null,
            parseBody(response?.body),
            response?.headers
        )
    }

    // Log the outcome only: never the token, claim number or patient details.
    logger.info('ACC submission finished', {
        action: 'acc_submission_result',
        metadata: {
            formId: request.formId,
            kind: result.kind,
            ...(result.kind === 'error'
                ? { statusCode: result.statusCode }
                : {}),
        },
    })

    return result
}
